import { useCallback } from 'react'
import type { Dispatch } from 'react'
import { CustomerAuthApiError, resetPassword } from '../../../customerAuthApi'
import type { ResetPasswordFlowAction } from './ResetPasswordFlow.types'

type ResetPasswordSubmitMessages = {
  submitError: string
}

type UseResetPasswordSubmitHandlersInput = {
  dispatch: Dispatch<ResetPasswordFlowAction>
  token: string
  password: string
  canSubmit: boolean
  messages: ResetPasswordSubmitMessages
}

type UseResetPasswordSubmitHandlersOutput = {
  handleSubmit: () => Promise<void>
}

export const useResetPasswordSubmitHandlers = ({
  dispatch,
  token,
  password,
  canSubmit,
  messages,
}: UseResetPasswordSubmitHandlersInput): UseResetPasswordSubmitHandlersOutput => {
  const handleSubmit = useCallback(async () => {
    dispatch({ type: 'SET_FORM_SUBMITTED', value: true })
    dispatch({ type: 'SET_PASSWORD_TOUCHED', value: true })
    dispatch({ type: 'SET_CONFIRM_PASSWORD_TOUCHED', value: true })
    dispatch({ type: 'SET_FORM_ERROR', value: '' })

    if (!canSubmit) return

    dispatch({ type: 'SET_SUBMITTING', value: true })
    try {
      await resetPassword(token, password)
      dispatch({ type: 'SET_SUCCESS', value: true })
    } catch (error) {
      if (error instanceof CustomerAuthApiError) {
        if (error.status === 410) {
          dispatch({ type: 'SET_PAGE_STATE', value: 'expired' })
          return
        }
        if (error.status === 400) {
          dispatch({ type: 'SET_PAGE_STATE', value: 'invalid' })
          return
        }
        if (error.message.trim()) {
          dispatch({ type: 'SET_FORM_ERROR', value: error.message.trim() })
          return
        }
      }
      dispatch({ type: 'SET_FORM_ERROR', value: messages.submitError })
    } finally {
      dispatch({ type: 'SET_SUBMITTING', value: false })
    }
  }, [canSubmit, dispatch, messages.submitError, password, token])

  return { handleSubmit }
}
